import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Grid, Snackbar, Alert, Dialog, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BuildingDetailsHotWaterAdd from './BuildingDetailsHotWaterAdd';

const BuildingDetailsHotWater = () => {
    const { id } = useParams();
    const [systems, setSystems] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);
    const [editSystem, setEditSystem] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    const fetchSystems = () => {
        axios.get(`/buildings/${id}/hot-water-systems/`)
            .then(res => setSystems(res.data))
            .catch(() => setSnackbar({ open: true, message: 'Failed to fetch hot water systems.', severity: 'error' }));
    };

    useEffect(() => {
        fetchSystems();
    }, [id]);

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => {
        setModalOpen(false);
        setEditSystem(null);
    };

    const handleSuccess = () => {
        fetchSystems();
        const message = editSystem
            ? 'Hot water system updated successfully!'
            : 'Hot water system added successfully!';
        setSnackbar({ open: true, message, severity: 'success' });
    };

    const handleError = () => {
        setSnackbar({ open: true, message: 'Failed to save hot water system.', severity: 'error' });
    };

    const handleDelete = (systemId) => {
        axios.delete(`/buildings/${id}/hot-water-systems/${systemId}/`)
            .then(() => {
                fetchSystems();
                setSnackbar({ open: true, message: 'Hot water system deleted successfully!', severity: 'success' });
            })
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to delete hot water system.', severity: 'error' });
            });
    };

    return (
        <Box sx={{ my: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Hot Water Systems</Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleModalOpen}>
                    Add Hot Water System
                </Button>
            </Box>

            {systems
                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                .map((system) => (
                    <Accordion
                        key={system.id}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            boxShadow: 'none',
                            border: '1px solid #e0e0e0',
                            '&::before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                                {system.system_type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditSystem(system);
                                    setModalOpen(true);
                                }}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{ mr: 2 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(system.id);
                                }}
                            >
                                Delete
                            </Button>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {Object.entries(system).map(([key, value]) => {
                                    if (
                                        key === 'id' || key === 'building' ||
                                        value === null || value === undefined || value === ''
                                    ) return null;

                                    const label = key
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, char => char.toUpperCase());

                                    return (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Typography variant="subtitle2">{label}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={Math.ceil(systems.length / itemsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                />
            </Box>

            <Dialog open={modalOpen} onClose={handleModalClose}>
                <BuildingDetailsHotWaterAdd
                    buildingId={id}
                    system={editSystem}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    onClose={handleModalClose}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BuildingDetailsHotWater;
